import React, { useEffect, useState } from 'react';

import { INITIAL_MESSAGES_SIZE } from '@/config/constants';
import { GetVideoQuery, useGetVideoQuery } from '@/graphql';
import { EThemePaletteTypes, useDarkMode } from '@/hooks/useDarkMode';
import { useAnalytics, useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { IonPage } from '@ionic/react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';

import { Loader } from '../../components/shared/Loader';
import { formatEnvironmentTitle } from '../../helpers/formatEnvironmentTitle';
import { useStatusBar } from '../../hooks/useStatusBar';
import { VideoPlayerContent } from './components/VideoPlayerContent';

type VideoPlayerParams = {
	token: string;
};

export const VideoPlayer: React.FC = () => {
	const { token } = useParams<VideoPlayerParams>() as VideoPlayerParams;
	const videoId = token;
	const [title, setTitle] = useState('');
	const { data, isError, error, isLoading, refetch } = useGetVideoQuery<
		GetVideoQuery,
		Error
	>(
		{
			getVideoId: videoId,
			input: {
				limit: INITIAL_MESSAGES_SIZE,
				offset: 0,
			},
		},
		{
			cacheTime: 0,
			refetchOnWindowFocus: false,
		},
	);

	const { isLoading: isEnvLoading, environmentString } = useEnvironment();
	const { isDarkModeOn, darkModeSetting } = useDarkMode();
	const { enableVideoMode } = useStatusBar();
	const { trackPage } = useAnalytics();

	const fireErrorEvent = useAnalyticsEvent('Error');

	// Store original palette before changing to dark mode for video
	const [originalPalette, setOriginalPalette] =
		useState<EThemePaletteTypes>();

	useEffect(() => {
		if (!isDarkModeOn) {
			setOriginalPalette(EThemePaletteTypes.LIGHT);
			enableVideoMode();
		}
	}, [isDarkModeOn, darkModeSetting, enableVideoMode]);

	useEffect(() => {
		if (data?.getVideo && !isEnvLoading) {
			const video = data?.getVideo;
			const contributor = video?.contributor?.name
				? ` | ${video.contributor.name}`
				: '';
			const videoName = video?.name ? ` | ${video.name}` : '';
			const fullTitle = `${formatEnvironmentTitle(
				environmentString,
			)}Asaph | Video ${contributor}${videoName}`;

			setTitle(fullTitle);
			trackPage({
				title: fullTitle,
			});
		}
	}, [data?.getVideo, environmentString, isEnvLoading, trackPage]);

	useEffect(() => {
		if (error) {
			fireErrorEvent({
				name: 'LoadVideoError',
				details: error,
			});
		}
	}, [error, fireErrorEvent]);

	const video = data?.getVideo;

	return (
		<>
			<Helmet title={title}>
				<style type="text/css">{`
          body {
            background-color: #000;
          }
          body .ion-page {
            top: 0;
          }
        `}</style>
			</Helmet>
			{isLoading || !video ? (
				<Loader type="night" />
			) : (
				<VideoPlayerContent
					error={error}
					video={video}
					refetchVideo={refetch}
					loading={isLoading}
					originalPalette={originalPalette}
				/>
			)}
		</>
	);
};
